"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import type { Dictionary } from "types";
import type { z } from "zod";

import { BRAND_CODE } from "@/config/common";
import { getMarketConfigFromLocale } from "@/config/market-configurations";
import { cn } from "@/lib/utils";

import { Heading } from "../ui";
import VoyadoFormInputField from "./VoyadoFormInputField";
import VoyadoFormSubmitButton from "./VoyadoFormSubmitButton";
import { useCreateVoyadoContact } from "./VoyadoSignUpForm.hooks";
import { voyadoContactSchema } from "./VoyadoSignUpForm.schema";
import VoyadoSignupFormSuccess from "./VoyadoSignupFormSuccess";

export default function VoyadoSignUpForm({
  className,
  locale,
  reference,
  dictionary,
}: {
  className?: string;
  locale?: string;
  reference?: string | null;
  dictionary: Dictionary;
}) {
  const marketConfig = getMarketConfigFromLocale(locale);
  const methods = useForm<
    z.input<typeof voyadoContactSchema>,
    any,
    z.output<typeof voyadoContactSchema>
  >({
    resolver: zodResolver(voyadoContactSchema as any),
    defaultValues: {
      countryCode: marketConfig.code,
    },
  });
  const {
    mutate: createVoyadoContactMutation,
    isSuccess,
    isPending,
    isError,
  } = useCreateVoyadoContact({
    label: reference ?? "voyado-sign-up-form",
  });

  const onSubmit = methods.handleSubmit((data) => {
    createVoyadoContactMutation({
      brandCode: BRAND_CODE,
      contact: {
        ...data,
        countryCode: data.countryCode ?? marketConfig.code,
        externalStoreId: marketConfig.externalStoreId,
        source: reference ?? "voyado-sign-up-form",
      },
    });
  });

  // If the user is already signed up we get an error with "Conflict" back from the CRM service.
  // We handle this error by a success message to the user, which is better than nothing happens / showing an error message.
  if (isSuccess || isError) {
    return <VoyadoSignupFormSuccess dictionary={dictionary} />;
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        className={cn("flex flex-col gap-2 bg-white p-4 text-black", className)}
      >
        {dictionary?.voyadoSignUpFormHeading && (
          <Heading
            className="lg:mb-3"
            size={{
              initial: 5,
              lg: 4,
            }}
          >
            {dictionary?.voyadoSignUpFormHeading}
          </Heading>
        )}
        <div className="grid grid-cols-2 gap-2">
          <VoyadoFormInputField
            type="text"
            name="firstName"
            label={dictionary?.voyadoSignUpFormFirstNameLabel ?? "First name"}
            required
          />
          <VoyadoFormInputField
            type="text"
            name="lastName"
            label={dictionary?.voyadoSignUpFormLastNameLabel ?? "Last name"}
            required
          />
        </div>
        <VoyadoFormInputField
          type="email"
          name="email"
          label={dictionary?.voyadoSignUpFormEmailLabel ?? "Email address"}
          required
        />
        <VoyadoFormInputField
          name="mobilePhone"
          type="tel"
          label={dictionary?.voyadoSignUpFormPhoneNumberLabel ?? "Phone number"}
        />
        <VoyadoFormSubmitButton isLoading={isPending}>
          {dictionary?.voyadoSignUpFormSubmitButton ?? "Sign up"}
        </VoyadoFormSubmitButton>
      </form>
    </FormProvider>
  );
}
